import {
  BUSINESS_KIND_LIST_LABELS,
  CONSENT_BASIS_LABELS,
  IMPORT_FILE_TYPE_LABELS,
  IMPORT_STATUS_LABELS,
  type ImportJobDTO,
  type LeadListResponse,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Mail, Store, X } from 'lucide-react';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, PageSpinner, Spinner, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { formatNumber, pluralize } from '../../lib/format';
import { Link, useNavigate, useParams } from 'react-router';
import { ImportFileInfo } from './ImportFileInfo';
import { ImportMapping } from './ImportMapping';
import { ImportReview } from './ImportReview';
import { IMPORT_STATUS_COLORS } from './ImportsPage';
import { KIND_LIST_PATHS, kindCount, kindNoun, kindPluralLabel } from '../leads/model';

const STEPS = ['Upload', 'Match columns', 'Import', 'Review'] as const;
// Past the last step: nothing is left to review.
const STEP_FOR: Record<ImportJobDTO['status'], number> = { uploaded: 1, validating: 2, importing: 2, validated: 3, completed: 4, failed: 2, cancelled: 1 };

export function ImportDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { data: job } = useQuery({
    queryKey: ['imports', id],
    queryFn: ({ signal }) => api.get<ImportJobDTO>(`/api/imports/${id}`, { signal }),
    refetchInterval: (query) => (query.state.data && ['validating', 'importing'].includes(query.state.data.status) ? 1_500 : false),
  });
  const cancel = useMutation({
    mutationFn: () => api.post<ImportJobDTO>(`/api/imports/${id}/cancel`),
    onSuccess: (next) => queryClient.setQueryData(['imports', id], next),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const settled = job?.status === 'validated' || job?.status === 'completed';
  // The import's leads as its list counts them (created + merged, still visible, of the import's kind), for the
  // hand-offs below; counted again each time a run settles (a review decision can add leads).
  const kinds = job ? [job.businessKind] : undefined;
  const { data: importLeads } = useQuery({
    queryKey: ['leads', 'list', { importJobId: id, kinds, pageSize: 1 }, job?.completedAt],
    queryFn: ({ signal }) => api.get<LeadListResponse>(apiUrl('/api/leads', { importJobId: id, kinds, pageSize: 1 }), { signal }),
    enabled: settled,
  });

  if (!job) return <PageSpinner />;
  const step = STEP_FOR[job.status];
  const running = job.status === 'validating' || job.status === 'importing';
  // Running with results already: a review decision is being applied, so what was imported stays on screen.
  const hasResults = job.createdCount + job.mergedCount + job.skippedCount + job.failedCount > 0;
  const showResults = settled || (running && hasResults);
  const leadCount = importLeads?.total ?? job.createdCount + job.mergedCount;
  const kind = job.businessKind;
  const plural = BUSINESS_KIND_LIST_LABELS[kind].toLowerCase();
  // The import's businesses live in the list of its kind (merges into other lists are not shown there).
  const leadsUrl = `${KIND_LIST_PATHS[kind]}?importJobId=${job.id}`;
  const outcome: [number, string][] = [
    [job.mergedCount, 'merged into existing leads'],
    [job.skippedCount, 'skipped'],
    [job.junkRows, 'set aside as junk'],
    [job.invalidRows, 'with problems (not imported)'],
    [job.failedCount, 'failed'],
  ];

  return (
    <Page width="wide">
      <PageHeader
        title={job.filename}
        breadcrumbs={[
          { label: 'Imports', to: '/imports' },
          ...(job.batchId ? [{ label: 'Folder import', to: `/imports/batch/${job.batchId}` }] : []),
          { label: job.filename },
        ]}
        meta={<Badge color={IMPORT_STATUS_COLORS[job.status]}>{IMPORT_STATUS_LABELS[job.status]}</Badge>}
        description={`${IMPORT_FILE_TYPE_LABELS[job.fileType]} · ${pluralize(job.totalRows, 'row')} · ${kindPluralLabel(kind)} · uploaded by ${job.createdBy?.name ?? 'someone'}`}
        actions={
          <div className="flex gap-2">
            {settled && (
              <a href={`/api/imports/${job.id}/report.csv`} download>
                <Button icon={Download}>Download report</Button>
              </a>
            )}
            {job.status === 'uploaded' && (
              <Button
                variant="ghost"
                icon={X}
                onClick={() =>
                  void (async () => {
                    if (await confirm({ title: 'Cancel this import?', message: 'Nothing has been imported yet.', confirmLabel: 'Cancel import', danger: true })) cancel.mutate();
                  })()
                }
              >
                Cancel import
              </Button>
            )}
          </div>
        }
      />

      <ol className="mb-4 flex flex-wrap gap-2 text-xs">
        {STEPS.map((label, index) => (
          <li key={label} className={`rounded-full px-3 py-1 ${index === step ? 'bg-ember-600 text-white' : index < step ? 'bg-ember-100 text-ember-800' : 'bg-slate-100 text-slate-500'}`}>
            {index + 1}. {label}
          </li>
        ))}
      </ol>

      {job.status === 'uploaded' && (
        <>
          <ImportFileInfo job={job} />
          <ImportMapping job={job} />
        </>
      )}

      {running && (
        <Card className="mb-4">
          <CardBody className="flex items-center gap-3 text-[13px] text-slate-600">
            <Spinner className="size-5" />
            {job.status === 'validating'
              ? `Checking ${pluralize(job.totalRows, 'row')}, setting junk aside and looking for ${kind === 'pizzeria' ? 'pizzerias' : 'businesses'} already in Hearth, then importing the ready ones… You can leave this page; it continues in the background.`
              : hasResults
                ? 'Applying your decisions… You can leave this page; it continues in the background.'
                : 'Importing… You can leave this page; it continues in the background.'}
          </CardBody>
        </Card>
      )}

      {showResults && (
        <>
          <Card className="mb-4">
            <CardBody className="space-y-3 text-[13px]">
              <p>
                <strong>{formatNumber(job.createdCount)}</strong> {job.createdCount === 1 ? kindNoun(kind) : plural} created
                {outcome
                  .filter(([count]) => count > 0)
                  .map(([count, label]) => (
                    <span key={label}>
                      {' '}
                      · <strong>{formatNumber(count)}</strong> {label}
                    </span>
                  ))}
                . Imported leads are tagged with this file as their source.
                {job.defaultConsentBasis !== 'none' && <> Contacts it added carry the email consent “{CONSENT_BASIS_LABELS[job.defaultConsentBasis]}”.</>}
              </p>
              {leadCount > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button icon={Store} onClick={() => navigate(leadsUrl)}>
                    {leadCount === 1 ? `See this ${kindNoun(kind)}` : `See these ${kindCount(kind, leadCount)}`}
                  </Button>
                  {can('email:bulk') && (
                    <Button
                      variant="primary"
                      icon={Mail}
                      onClick={() =>
                        navigate('/email/bulk', {
                          state: {
                            selection: { mode: 'filter', filter: { importJobId: job.id, kinds: [kind] }, excludeIds: [] },
                            count: leadCount,
                            description: `${BUSINESS_KIND_LIST_LABELS[kind]} from import “${job.filename}”`,
                          },
                        })
                      }
                    >
                      Email these {plural}
                    </Button>
                  )}
                </div>
              )}
              {leadCount > 0 && job.defaultConsentBasis === 'none' && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                  No email consent was recorded with this file, so bulk email skips these leads under Canada’s anti-spam law (CASL) until consent is recorded. If you know
                  where the addresses came from — published on their website, customers, recent inquiries — open{' '}
                  <Link to={leadsUrl} className="font-medium underline">
                    these {plural}
                  </Link>
                  , select them and choose <strong>Record email consent</strong>.
                </p>
              )}
              {settled && job.unresolvedDuplicates > 0 && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 font-medium text-amber-900">
                  {pluralize(job.unresolvedDuplicates, 'possible duplicate')} {job.unresolvedDuplicates === 1 ? 'needs' : 'need'} your decision — pick Create, Merge or Skip below;
                  each choice is applied right away.
                </p>
              )}
              {settled && job.junkRows > 0 && (
                <p className="text-slate-600">
                  The scan set {pluralize(job.junkRows, 'row')} aside as junk. If it got any wrong, import them anyway from the Junk tab.
                </p>
              )}
            </CardBody>
          </Card>
          <ImportReview
            job={job}
            filters={[
              { key: 'duplicate', count: job.duplicateRows },
              { key: 'junk', count: job.junkRows },
              { key: 'invalid', count: job.invalidRows },
              { key: 'imported', count: job.createdCount },
              { key: 'merged', count: job.mergedCount },
              { key: 'skipped', count: job.skippedCount },
              { key: 'failed', count: job.failedCount },
            ]}
          />
        </>
      )}

      {(job.status === 'cancelled' || job.status === 'failed') && (
        <Card>
          <CardBody className="text-[13px] text-slate-600">{job.status === 'failed' ? `The import failed: ${job.error ?? 'unknown error'}` : 'This import was cancelled. Nothing was imported.'}</CardBody>
        </Card>
      )}
    </Page>
  );
}
